import { SERVER_URL } from '@qima-labs/app/shared/constants/app.constants';
import { IHttpResponseBody } from '@qima-labs/app/shared/models/interfaces/http-response-body.interface';
import { EStorageKeys } from '@qsp-ui-library/models/enums/storage-keys.enum';
import { StorageService } from '@qsp-ui-library/services/storage-services/storage.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public constructor(
    private readonly _httpClient: HttpClient,
    private readonly _storageService: StorageService,
    private readonly _router: Router
  ) {}

  public getUserPermissionsArray$(): Observable<string[]> {
    // if user permissions changed ,need user to login again
    if (this._storageService.getItem(EStorageKeys.USER_PERMISSIONS) === '') {
      return this._getUserPermissions$().pipe(
        switchMap((permissions: string[]): Observable<string[]> => {
          if (!permissions.length) {
            return this._redirectNoAuthorized$();
          }

          this._storageService.setItem(EStorageKeys.USER_PERMISSIONS, JSON.stringify(permissions));

          return of(permissions);
        })
      );
    }

    if (this._storageService.getItem(EStorageKeys.USER_PERMISSIONS) === '[]') {
      return this._redirectNoAuthorized$();
    }

    return of(JSON.parse(this._storageService.getItem(EStorageKeys.USER_PERMISSIONS) || '[]'));
  }

  public saveUserPermissionsToStore(redirectUrl?: string): void {
    this._getUserPermissions$().subscribe({
      next: (userPermissions: string[]): void => {
        if (!userPermissions.length) {
          void this._router.navigate(['/unauthorized']);

          return;
        }

        this._storageService.setItem(EStorageKeys.USER_PERMISSIONS, JSON.stringify(userPermissions));
        void this._router.navigateByUrl(redirectUrl || '/');
      },
      error: (): void => {
        void this._router.navigate(['/unauthorized']);
      },
    });
  }

  /**
   * Check if all provided keys exist in user permissions
   * @param {string[]} keys - Array of permission keys to check
   * @returns {boolean} - Returns true if all keys exist in permissions, false otherwise
   */
  public hasPermissions(keys: string[]): boolean {
    if (!keys || keys.length === 0) {
      return false;
    }

    const storedPermissions = this._storageService.getItem(EStorageKeys.USER_PERMISSIONS);

    if (!storedPermissions || storedPermissions === '' || storedPermissions === '[]') {
      return false;
    }

    try {
      const userPermissions: string[] = JSON.parse(storedPermissions);

      if (!userPermissions || userPermissions.length === 0) {
        return false;
      }

      return keys.every((key: string): boolean => userPermissions.includes(key));
    } catch {
      return false;
    }
  }

  private _getUserPermissions$(): Observable<string[]> {
    const url = `${SERVER_URL}/auth2-service-cloud/v1.0/permission/module-resources/module-name/lab-service-cloud/users/current-user`;

    return this._httpClient
      .get<IHttpResponseBody<string[]>>(url, { observe: 'body' })
      .pipe(map((response: Readonly<IHttpResponseBody<string[]>>): string[] => response.content ?? []));
  }

  private _redirectNoAuthorized$(): Observable<string[]> {
    void this._router.navigate(['/unauthorized']);

    return of([]);
  }
}
